import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import AddEditAccessoryModal from './AddEditAccessoryModal';
import ImageViewerModal from './ImageViewerModal';
import { getImageUrl, getThumbnailUrl } from '../../utils/api';
import './Salon.css';

const ManageAccessories = ({ salonId, userToken, userRole, currentPage: urlPage = 1, onPageChange }) => {
  const { t } = useLanguage();
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImage, setViewerImage] = useState('');

  const itemsPerPage = 10;

  // Sync URL page with local state
  useEffect(() => {
    setCurrentPage(urlPage);
  }, [urlPage]);

  // Helper function to format type display
  const formatTypeDisplay = (type) => {
    const typeMap = {
      'handbag': t('accessories.handbag'),
      'coat': t('accessories.coat'),
      'wrap': t('accessories.wrap'),
      'bolero': t('accessories.bolero'),
      'tiara': t('accessories.tiara'),
      'sash': t('accessories.sash'),
      'fur_jacket': t('accessories.furJacket'),
      'veil': t('accessories.veil'),
      'rest': 'Rest'
    };
    return typeMap[type] || type;
  };

  const fetchAccessories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/salons/${salonId}/accessories`, {
        headers: { Authorization: `Bearer ${userToken}` },
        params: {
          page: currentPage,
          limit: itemsPerPage,
          sortField: sortField,
          sortDirection: sortDirection
        }
      });
      
      setAccessories(response.data.accessories);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err) {
      console.error('Error fetching accessories:', err);
      setError('Failed to fetch accessories');
    } finally {
      setLoading(false);
    }
  }, [salonId, currentPage, userToken, sortField, sortDirection]);

  useEffect(() => {
    fetchAccessories();
  }, [fetchAccessories]);


  const handleAddAccessory = () => {
    setSelectedAccessory(null);
    setIsModalOpen(true);
  };

  const handleEditAccessory = (accessory) => {
    setSelectedAccessory(accessory);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAccessory(null);
  };

  const handleAccessoryAdded = () => {
    fetchAccessories();
    handleModalClose();
  };

  const handleAccessoryUpdated = () => {
    fetchAccessories();
    handleModalClose();
  };

  const handleImageClick = (accessory) => {
    if (accessory.image) {
      // Convert relative path to full URL if needed
      const fullImageUrl = getImageUrl(accessory.image);
      setViewerImage(fullImageUrl);
      setShowImageViewer(true);
    }
  };

  const handleDeleteAccessory = async (accessoryId) => {
    if (!window.confirm(t('messages.confirm.deleteAccessory'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/accessories/${accessoryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchAccessories();
    } catch (error) {
      console.error('Error deleting accessory:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete accessory';
      alert(errorMessage);
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, set default direction based on field type
      setSortField(field);
      
      // Numerical fields default to descending, text fields to ascending
      const descendingFields = ['stock_count'];
      const defaultDirection = descendingFields.includes(field) ? 'desc' : 'asc';
      
      setSortDirection(defaultDirection);
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t('common.previous')}
        </button>
        {pages}
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="manage-page">
        <div className="loading">{t('messages.loading.accessories')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-page">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="manage-page">
      {/* Header */}
      <div className="table-header">
        <h3>{t('accessories.title')}</h3>
        <button
          className="salon-button primary"
          onClick={handleAddAccessory}
        >
          {t('accessories.addAccessory')}
        </button>
      </div>


      {/* Table */}
      {accessories.length === 0 ? (
        <div className="table-empty-state">
          <div className="table-empty-state-icon">📦</div>
          <h4>{t('accessories.noAccessories')}</h4>
          <p>{t('accessories.addFirstAccessory', 'Start building your accessory inventory by adding your first accessory.')}</p>
          <button
            className="salon-button primary"
            onClick={handleAddAccessory}
          >
            {t('accessories.addAccessory')}
          </button>
        </div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="data-table accessories-table">
              <thead>
                <tr>
                  <th 
                    className="sortable fixed-column" 
                    onClick={() => handleSort('name')}
                    title={`${t('common.tooltips.clickToSort')} ${t('accessories.accessoryName').toLowerCase()}`}
                  >
                    {t('accessories.accessoryName')}
                    {sortField === 'name' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('type')}
                  title={`${t('common.tooltips.clickToSort')} ${t('common.type').toLowerCase()}`}
                >
                  {t('common.type')}
                  {sortField === 'type' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('stock_count')}
                  title={`${t('common.tooltips.clickToSort')} ${t('accessories.stockCount').toLowerCase()}`}
                >
                  {t('accessories.stockCount')}
                  {sortField === 'stock_count' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {accessories.map((accessory) => (
                <tr key={accessory.id}>
                  <td className="fixed-column">
                    <div className="table-cell-content">
                      <div className="accessory-info-row">
                        {accessory.image ? (
                          <img
                            src={getThumbnailUrl(accessory.image)}
                            alt={accessory.name}
                            className="table-preview-image clickable"
                            onClick={() => handleImageClick(accessory)}
                            title={t('common.tooltips.viewImage')}
                          />
                        ) : (
                          <div className="table-preview-image-placeholder">
                            <span className="table-preview-image-placeholder-icon">📦</span>
                          </div>
                        )}
                        <div className="accessory-name-container">
                          <div className="accessory-name">{accessory.name}</div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="accessory-type">
                        {formatTypeDisplay(accessory.type)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="stock-count">
                        {accessory.stock_count || 0}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="table-actions">
                        <button 
                          className="action-button edit"
                          onClick={() => handleEditAccessory(accessory)}
                          title={t('accessories.editAccessory')}
                        >
                          ✏️
                        </button>
                        <button 
                          className="action-button delete"
                          onClick={() => handleDeleteAccessory(accessory.id)}
                          title={t('accessories.deleteAccessory')}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="pagination-container">
        {renderPagination()}
      </div>

      {/* Add/Edit Modal */}
      <AddEditAccessoryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        salonId={salonId}
        accessory={selectedAccessory}
        onAccessoryAdded={handleAccessoryAdded}
        onAccessoryUpdated={handleAccessoryUpdated}
      />

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={showImageViewer}
        onClose={() => setShowImageViewer(false)}
        images={[viewerImage]}
        currentIndex={0}
      />
    </div>
  );
};

export default ManageAccessories;
